import { PrescriptionCancelButton } from '@/features/prescription-cancellation';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useStore } from '@tanstack/react-form';
import { useQueryClient } from '@tanstack/react-query';

import {
  PriorAuthCommandRejectionT,
  PriorAuthGateOutcomeT,
  type MedicationDecisionT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { AgentTweakableArea } from '@/features/copilot/agent-tweakable';
import { useProposedActionMutations } from '@/features/proposed-actions-plan/model/useProposedActionMutations';
import { Button } from '@/shared/ui/shadcn/button';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';
import { Checkbox } from '@/shared/ui/shadcn/checkbox';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import { analytics, ANALYTICS_EVENTS, isRevisionFlow, logger } from '@/shared/lib';
import { pushZodIssuesToTanStackForm } from '@/shared/lib/forms';
import { getTodayYYYYMMDD } from '@/shared/lib/dates';
import { usePatientFormularyQuery, usePreviewPriorAuthQuery } from '@/entities/rx';
import { useCopilotOverlays } from '@/shared/stores/ui';
import { extractApiErrorDetail } from '@/shared/lib/errors/extractApiErrorDetail';
import { useRxWizardAgentIntegration } from '../agent';
import { applySelectedMedicationToForm } from '../model/applySelectedMedicationToForm';
import {
  fetchPrefillMedication,
  resolvePrefillPharmacy,
  retryRxPrefill,
  type RxPrefillIssue,
} from '../model/prefillLookups';
import { usePatientFormularyParams } from '../model/usePatientFormularyParams';

import type { RxWizardStepId } from '../model/types';
import { useRxWizardForm } from '../model/useRxWizardForm';
import { usePriorAuthBannerModel } from '../model/usePriorAuthBannerModel';
import type { RxSendDisposition, RxSendResult } from '../model/submission/orchestrator';
import { useRxSubmissionOrchestrator } from '../model/submission/orchestrator';
import {
  medicationDecisionActions,
  requiresDiagnosisForInsuranceApproval,
  resolveMedicationDecisionOutcome,
} from '../model/medicationDecisionActions';
import type { RxWizardPrefill } from '../model/rxWizardPrefill';
import { RxPrefillIssueAlert } from './RxPrefillIssueAlert';
import { RxWizardProgress } from './RxWizardProgress';
import { DiagnosisSelector } from './selectors/DiagnosisSelector';
import { PharmacySelector } from './selectors/PharmacySelector';
import { DetailsStep } from './steps/DetailsStep';
import { ReviewStep } from './steps/ReviewStep';
import { ConfirmationStep } from './steps/ConfirmationStep';

/**
 * Wizard step titles. We swap the confirmation title based on the preview
 * PA outcome: a ``PA_REQUIRED`` draft starts a PA instead of going to the
 * pharmacy, so saying "Prescription sent" would be incorrect.
 */
const WIZARD_TITLE_BY_STEP: Record<RxWizardStepId, string> = {
  details: 'Prescription details',
  review: 'Prescription',
  confirmation: 'Prescription sent',
};
const PA_CONFIRMATION_TITLE = 'Prior auth sent';
const EXCLUDED_CONFIRMATION_TITLE = 'Prescription signed';

/**
 * Fire the appropriate success toast for a completed send. We branch on the
 * backend-reported disposition rather than the pre-submit preview so the
 * message always tells the truth about what happened — including surfacing
 * the PA case number when the backend returns one.
 */
function showSendSuccessToast(disposition: RxSendDisposition | null): void {
  if (disposition?.kind === 'confirmation-pending') {
    toast.info('Waiting for confirmation.');
    return;
  }
  if (disposition?.kind === 'prior-auth-initiated') {
    toast.success('Prior auth sent.', {
      description: disposition.caseNumber ? `Case #${disposition.caseNumber}.` : undefined,
    });
    return;
  }
  if (disposition?.kind === 'excluded') {
    toast.warning('Prescription needs clinician action.', {
      description: 'Drug is excluded from the payer formulary.',
    });
    return;
  }
  if (disposition?.kind === 'divert-failed') {
    toast.warning('Prescription submitted with warnings.', {
      description: disposition.error,
    });
    return;
  }
  toast.success('Prescription sent.');
}

function SubmittingLabel({ decision }: { decision: MedicationDecisionT }) {
  return (
    <>
      <Loader2 className="animate-spin" aria-hidden />
      {decision === 'START_PA' ? 'Requesting approval…' : 'Sending…'}
    </>
  );
}

const PREV_STEP: Partial<Record<RxWizardStepId, RxWizardStepId>> = {
  review: 'details',
  confirmation: 'review',
};

const EDIT_STEP_IDS: ReadonlySet<RxWizardStepId> = new Set(['details']);

interface RxWizardProps {
  userId: string;
  prefill: RxWizardPrefill | null;
}

function RxWizardInner({ userId, prefill }: RxWizardProps) {
  const overlays = useCopilotOverlays();
  const { form, resetForKind, validateDraft, buildRequest } = useRxWizardForm();
  const [stepId, setStepId] = useState<RxWizardStepId>('details');
  const [formError, setFormError] = useState<string | null>(null);
  const [paExpedited, setPaExpedited] = useState(false);
  const [lastDecision, setLastDecision] = useState<MedicationDecisionT>('SEND_NO_PA');
  const [decisionDialog, setDecisionDialog] = useState<
    'SELF_PAY' | 'EXCEPTIONAL_UNKNOWN_SEND' | null
  >(null);
  const [decisionReason, setDecisionReason] = useState('');
  const [patientAgreed, setPatientAgreed] = useState(false);
  const [diagnosisDialogOpen, setDiagnosisDialogOpen] = useState(false);
  const [canChooseUpdatedDecision, setCanChooseUpdatedDecision] = useState(false);
  // Optional override that pins the formulary lookup to a specific
  // ``PatientEligibilityId`` instead of letting the server pick the
  // first active coverage. Lives outside the form draft because it's
  // not part of the prescription request — it just steers the lookup
  // shown in the Details / Review steps. ``null`` means "auto".
  const [formularyEligibilityOverride, setFormularyEligibilityOverride] = useState<number | null>(
    null
  );

  const orchestrator = useRxSubmissionOrchestrator();
  const {
    state: submissionState,
    isDraftLocked,
    isSubmitting,
    reset: resetSubmission,
  } = orchestrator;
  const mutations = useProposedActionMutations();

  useRxWizardAgentIntegration(form);
  const queryClient = useQueryClient();
  // ``prefill`` arrives as a prop (the overlay carries it and remounts on each
  // open). It's consumed once by the hydration effect below.
  const sourceProposedActionRef = useRef<NonNullable<
    RxWizardPrefill['sourceProposedAction']
  > | null>(null);
  const existingRxIdRef = useRef<string | null>(null);
  const lastAutoDispenseUnitIdRef = useRef<string | null>(null);
  const [isEditableMode, setIsEditableMode] = useState(true);
  const [isHydrating, setIsHydrating] = useState(false);
  const [prefillIssue, setPrefillIssue] = useState<RxPrefillIssue | null>(null);
  const [isRetryingPrefill, setIsRetryingPrefill] = useState(false);
  // Stays on once shown, so a clinician who picked a pharmacy can still change it.
  const [offerPharmacyPicker, setOfferPharmacyPicker] = useState(false);
  const selectedPharmacy = useStore(form.store, (state) => state.values.selectedPharmacy);
  const blockingPrefillIssue: RxPrefillIssue | null =
    prefillIssue && (prefillIssue.medicationFailed || (prefillIssue.pharmacy && !selectedPharmacy))
      ? {
          medicationFailed: prefillIssue.medicationFailed,
          pharmacy: selectedPharmacy ? null : prefillIssue.pharmacy,
        }
      : null;
  // The details and review steps observe this same query, so react-query
  // dedupes it to one request. A current formulary PA flag is enough to make
  // the approval action available without waiting for the slower benefits
  // preview. Placeholder data is deliberately ignored after a medication or
  // coverage change so a previous drug's restriction cannot leak forward.
  const formularyParams = usePatientFormularyParams(form, formularyEligibilityOverride);
  const formularyQuery = usePatientFormularyQuery({ userId, params: formularyParams });
  const formularyRequiresInsuranceApproval =
    !formularyQuery.isPlaceholderData &&
    formularyQuery.data?.formulary?.Restrictions?.HasPriorAuthorization === true;

  // Preview PA outcome — the `ReviewStep` already calls the same hook, so
  // react-query dedupes this to a single in-flight request. This drives
  // *pre-submit* copy only (button label, "Requesting approval…" spinner) where we
  // haven't yet heard back from the server. Post-submit copy reads from the
  // backend's actual send response via ``submissionState.sendDisposition``
  // so the toast / confirmation always tell the truth — even when the
  // Rx-time gate disagrees with the preview (e.g. formulary data moved
  // between preview and send).
  const priorAuthParams = usePriorAuthBannerModel(form.state.values, formularyEligibilityOverride);
  const priorAuthPreviewQuery = usePreviewPriorAuthQuery({
    userId,
    params: priorAuthParams,
  });
  // Both ``PA_REQUIRED`` and ``EXCLUDED`` divert the Rx away from the
  // pharmacy — the former auto-initiates a PA case, the latter opens a
  // clinician-action request. ``UNKNOWN`` requires an explicit exceptional send.
  const previewOutcome =
    priorAuthPreviewQuery.isError || priorAuthPreviewQuery.isPlaceholderData
      ? null
      : (priorAuthPreviewQuery.data?.outcome ?? null);
  const decisionOutcome = resolveMedicationDecisionOutcome(
    previewOutcome,
    formularyRequiresInsuranceApproval
  );
  const isPriorAuthPreview = decisionOutcome === PriorAuthGateOutcomeT.PA_REQUIRED;
  const isExcludedPreview = decisionOutcome === PriorAuthGateOutcomeT.EXCLUDED;
  const decisionActions = medicationDecisionActions(
    decisionOutcome,
    priorAuthPreviewQuery.data?.patientEligibilityId != null
  );
  const diagnosisRequiredForInsuranceApproval = requiresDiagnosisForInsuranceApproval(
    formularyRequiresInsuranceApproval,
    decisionActions
  );
  const isDiagnosisMissingForInsuranceApproval =
    diagnosisRequiredForInsuranceApproval && form.state.values.selectedDiagnosis == null;

  // After a completed send, the server tells us exactly what happened. Use
  // that when it's available; otherwise fall back to the preview signal so
  // the button labels and the "Requesting approval…" spinner still match the best
  // information we have pre-submit.
  const sendDisposition = submissionState.sendDisposition;
  const isPriorAuthResolved = sendDisposition
    ? sendDisposition.kind === 'prior-auth-initiated'
    : isPriorAuthPreview;
  const isExcludedResolved = sendDisposition
    ? sendDisposition.kind === 'excluded'
    : isExcludedPreview;
  const wasDiverted = isPriorAuthResolved || isExcludedResolved;

  const reportProposedActionSuccess = useCallback(
    (args?: { dosespotPrescriptionId?: string; disposition?: RxSendDisposition }) => {
      const source = sourceProposedActionRef.current;
      if (!source) {
        return;
      }
      const selectedPharmacy = form.state.values.selectedPharmacy;
      mutations
        .finalizeFromSource(source, {
          ...(args?.dosespotPrescriptionId
            ? { dosespotPrescriptionId: args.dosespotPrescriptionId }
            : {}),
          ...(args?.disposition ? { sendDisposition: args.disposition.kind } : {}),
          ...(selectedPharmacy?.PharmacyId != null
            ? { pharmacyId: selectedPharmacy.PharmacyId }
            : {}),
          ...(selectedPharmacy?.StoreName ? { pharmacyName: selectedPharmacy.StoreName } : {}),
        })
        .catch((err: unknown) => {
          logger.error('rxWizard.finalizeFromSource.failed', {
            externalId: source.externalId,
            err,
          });
        });
    },
    [form.state.values.selectedPharmacy, mutations]
  );

  const analyticsContext = useMemo(
    () => ({ actor: 'clinician' as const, patientUserId: userId }),
    [userId]
  );

  const wizardOpenedRef = useRef(false);
  useEffect(() => {
    if (wizardOpenedRef.current) {
      return;
    }
    wizardOpenedRef.current = true;
    void analytics.track({
      name: ANALYTICS_EVENTS.rx.wizardOpened,
      context: analyticsContext,
    });
  }, [analyticsContext]);

  const prevStepRef = useRef(stepId);
  useEffect(() => {
    if (prevStepRef.current === stepId) {
      return;
    }
    prevStepRef.current = stepId;
    void analytics.track({
      name: ANALYTICS_EVENTS.rx.wizardStepViewed,
      context: analyticsContext,
      properties: { stepId },
    });
  }, [stepId, analyticsContext]);

  useEffect(() => {
    if (!prefill) {
      return;
    }
    if (isDraftLocked) {
      return;
    }

    let cancelled = false;
    setIsHydrating(true);
    void (async () => {
      try {
        if (prefill.sourceProposedAction) {
          sourceProposedActionRef.current = prefill.sourceProposedAction;
        }
        if (prefill.existingDosespotPrescriptionId) {
          existingRxIdRef.current = prefill.existingDosespotPrescriptionId;
        }

        const isRevision = isRevisionFlow(prefill.sourceProposedAction);
        const isExecuteOnly =
          (!!prefill.sourceProposedAction && !isRevision) ||
          !!prefill.existingDosespotPrescriptionId;
        setIsEditableMode(!isExecuteOnly);

        resetSubmission();
        setCanChooseUpdatedDecision(false);
        resetForKind('coded');
        setFormError(null);
        setPrefillIssue(null);
        setOfferPharmacyPicker(false);
        setStepId(isRevision ? 'details' : 'review');

        // Apply scalar prefill fields immediately so they are never blocked by
        // async medication/pharmacy lookups.
        form.setFieldValue('directions', prefill.directions);
        form.setFieldValue('quantity', prefill.quantity);
        form.setFieldValue('refills', prefill.refills);
        form.setFieldValue('daysSupply', prefill.daysSupply);
        form.setFieldValue('pharmacyNotes', prefill.pharmacyNotes ?? '');
        form.setFieldValue('noSubstitutions', prefill.noSubstitutions ?? false);
        form.setFieldValue('effectiveDate', prefill.effectiveDate ?? getTodayYYYYMMDD());

        const medication = await fetchPrefillMedication(queryClient, prefill.dispensableDrugId);
        if (cancelled) {
          return;
        }
        if (medication) {
          applySelectedMedicationToForm({ form, selected: medication, lastAutoDispenseUnitIdRef });
        }

        const pharmacy = await resolvePrefillPharmacy(queryClient, userId, prefill.pharmacyId);
        if (cancelled) {
          return;
        }
        if (pharmacy.kind === 'resolved') {
          form.setFieldValue('selectedPharmacy', pharmacy.pharmacy);
        }

        // Execute-only mode hides the editable details, so a lookup failure is
        // surfaced for the clinician to retry, pick a pharmacy, or reject.
        // Re-sending an existing DoseSpot prescription doesn't need either
        // lookup: the prescription already carries its medication and pharmacy.
        const hasExistingRx = !!prefill.existingDosespotPrescriptionId;
        const pharmacyIssue = pharmacy.kind === 'unresolved' ? pharmacy.issue : null;
        if (isExecuteOnly && !hasExistingRx && (!medication || pharmacyIssue)) {
          setPrefillIssue({ medicationFailed: !medication, pharmacy: pharmacyIssue });
          setOfferPharmacyPicker(pharmacyIssue !== null);
        }
      } finally {
        if (!cancelled) {
          setIsHydrating(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [form, isDraftLocked, resetSubmission, prefill, queryClient, resetForKind, userId]);

  const retryPrefill = async () => {
    if (!prefill || !prefillIssue) {
      return;
    }
    setIsRetryingPrefill(true);
    try {
      const remainingIssue = await retryRxPrefill({
        queryClient,
        userId,
        dispensableDrugId: prefill.dispensableDrugId,
        pharmacyId: prefill.pharmacyId,
        issue: prefillIssue,
        hasSelectedPharmacy: () => form.state.values.selectedPharmacy != null,
        applyMedication: (medication) =>
          applySelectedMedicationToForm({ form, selected: medication, lastAutoDispenseUnitIdRef }),
        applyPharmacy: (pharmacy) => form.setFieldValue('selectedPharmacy', pharmacy),
      });
      setPrefillIssue(remainingIssue);
    } finally {
      setIsRetryingPrefill(false);
    }
  };

  const rejectSourceProposedAction = async (reason: string) => {
    const source = sourceProposedActionRef.current;
    if (!source) {
      return;
    }
    try {
      await mutations.reject(source.externalId, reason);
    } catch (err: unknown) {
      logger.error('rxWizard.reject.failed', { externalId: source.externalId, err });
      toast.error('Could not reject prescription.', { description: extractApiErrorDetail(err) });
      return;
    }
    overlays.close('rxWizard');
  };

  const validateDetailsAndStayOrAdvance = async () => {
    setFormError(null);

    // Populate TanStack Form errors based on validators:
    await form.validateAllFields('submit');

    const result = validateDraft();
    if (result.success) {
      setStepId('review');
      return;
    }

    // Push Zod errors into TanStack Form field meta so adapters display them.
    pushZodIssuesToTanStackForm(form, result.error.issues);

    const topLevel = result.error.issues.find((i) => i.path?.[0] === '_form')?.message;
    setFormError(topLevel ?? 'Complete the highlighted prescription details before continuing.');
    setStepId('details');
  };

  const handleBack = () => {
    const prev =
      stepId === 'confirmation' && submissionState.status === 'failed-create'
        ? 'review'
        : (PREV_STEP[stepId] ?? 'details');
    if (isDraftLocked && EDIT_STEP_IDS.has(prev)) {
      return;
    }
    setFormError(null);
    setStepId(prev);
  };

  const handleNext = async () => {
    if (stepId === 'details') {
      await validateDetailsAndStayOrAdvance();
    }
  };

  const recoverFromStaleCoverage = async (sendResult: RxSendResult | null): Promise<boolean> => {
    if (
      sendResult?.kind !== 'rejected' ||
      sendResult.rejectionCode !== PriorAuthCommandRejectionT.DECISION_NOT_ALLOWED
    ) {
      return false;
    }

    setCanChooseUpdatedDecision(true);
    setStepId('review');
    const refreshed = await priorAuthPreviewQuery.refetch();
    if (refreshed.isError) {
      toast.error('Coverage changed, but the current options could not be loaded.');
    } else {
      toast.info('Coverage changed. Review the current options.');
    }
    return true;
  };

  const handleSubmit = async (
    decision: MedicationDecisionT,
    context?: { reason?: string; patientAgreed?: boolean }
  ) => {
    setLastDecision(decision);
    setFormError(null);

    if (decision === 'START_PA' && form.state.values.selectedDiagnosis == null) {
      setFormError('Select the condition being treated before requesting insurance approval.');
      return;
    }

    await form.validateAllFields('submit');

    const result = buildRequest();
    if (!result.success) {
      pushZodIssuesToTanStackForm(form, result.error.issues);
      setFormError(
        isEditableMode
          ? 'Complete the highlighted prescription details before submitting.'
          : 'Prescription details could not be fully loaded for this execute-only action.'
      );
      if (isEditableMode) {
        setStepId('details');
      }
      return;
    }

    const rxKind = form.state.values.kind;
    void analytics.track({
      name: ANALYTICS_EVENTS.rx.submitAttempted,
      context: analyticsContext,
      properties: {
        kind: rxKind,
        hasPharmacyNotes: (form.state.values.pharmacyNotes ?? '').trim().length > 0,
        noSubstitutions: form.state.values.noSubstitutions,
      },
    });

    const existingRxId = existingRxIdRef.current;
    const sourceProposedActionExternalId = sourceProposedActionRef.current?.externalId ?? null;

    let dosespotPrescriptionId = submissionState.dosespotPrescriptionId;
    let prescriptionRecordId = submissionState.prescriptionRecordId;
    if (existingRxId) {
      setFormError('Open the prior authorization to send this prescription.');
      return;
    } else if (!dosespotPrescriptionId || !prescriptionRecordId) {
      const diag = form.state.values.selectedDiagnosis;
      const created = await orchestrator.create({
        userId,
        rxRequest: result.data,
        sourceProposedActionExternalId,
        diagnosis: diag
          ? {
              diagnosisId: diag.diagnosisId,
              diagnosisCode: diag.code,
              diagnosisDescription: diag.name,
            }
          : undefined,
      });
      if (!created) {
        toast.error('Could not create prescription.', {
          description: orchestrator.state.errorMessage ?? undefined,
        });
        void analytics.track({
          name: ANALYTICS_EVENTS.rx.submitResult,
          context: analyticsContext,
          properties: { kind: rxKind, outcome: 'failure' },
        });
        setStepId('confirmation');
        return;
      }
      dosespotPrescriptionId = created.dosespotPrescriptionId;
      prescriptionRecordId = created.prescriptionRecordId;
    }

    setCanChooseUpdatedDecision(false);
    const sendResult = await orchestrator.send({
      userId,
      dosespotPrescriptionId,
      prescriptionRecordId,
      decision,
      reason: context?.reason,
      patientAgreed: context?.patientAgreed,
      ...(decision === 'START_PA'
        ? {
            patientEligibilityId: formularyEligibilityOverride,
            expedited: paExpedited,
          }
        : {}),
    });

    if (await recoverFromStaleCoverage(sendResult)) {
      void analytics.track({
        name: ANALYTICS_EVENTS.rx.submitResult,
        context: analyticsContext,
        properties: { kind: rxKind, outcome: 'failure' },
      });
      return;
    }

    const disposition = sendResult?.kind === 'completed' ? sendResult.disposition : null;
    if (disposition?.kind === 'confirmation-pending') {
      showSendSuccessToast(disposition);
    } else if (disposition) {
      showSendSuccessToast(disposition);
      reportProposedActionSuccess({ dosespotPrescriptionId, disposition });
      overlays.close('rxWizard');
    } else {
      toast.error(
        decision === 'START_PA'
          ? 'Could not initiate prior authorization.'
          : 'Could not send prescription.',
        {
          description:
            sendResult?.kind === 'rejected'
              ? sendResult.message
              : (orchestrator.state.errorMessage ?? undefined),
        }
      );
    }

    void analytics.track({
      name: ANALYTICS_EVENTS.rx.submitResult,
      context: analyticsContext,
      properties: { kind: rxKind, outcome: disposition !== null ? 'success' : 'failure' },
    });

    setStepId('confirmation');
  };

  const prevStepId =
    stepId === 'confirmation' && submissionState.status === 'failed-create'
      ? 'review'
      : (PREV_STEP[stepId] ?? 'details');
  const isBackDisabled =
    stepId === 'details' ||
    (isDraftLocked && EDIT_STEP_IDS.has(prevStepId)) ||
    (!isEditableMode && stepId === 'review');

  const retryCreate = async () => {
    await handleSubmit(lastDecision, {
      reason: decisionReason.trim() || undefined,
      patientAgreed,
    });
  };

  const retrySend = async () => {
    const sendResult = await orchestrator.retrySend(userId, {
      decision: lastDecision,
      reason: decisionReason.trim() || undefined,
      patientAgreed,
      ...(lastDecision === 'START_PA'
        ? {
            patientEligibilityId: formularyEligibilityOverride,
            expedited: paExpedited,
          }
        : {}),
    });
    if (await recoverFromStaleCoverage(sendResult)) {
      return;
    }

    const disposition = sendResult?.kind === 'completed' ? sendResult.disposition : null;
    if (disposition?.kind === 'confirmation-pending') {
      showSendSuccessToast(disposition);
    } else if (disposition) {
      showSendSuccessToast(disposition);
      reportProposedActionSuccess({
        dosespotPrescriptionId: orchestrator.state.dosespotPrescriptionId ?? undefined,
        disposition,
      });
      overlays.close('rxWizard');
    } else {
      toast.error(
        lastDecision === 'START_PA'
          ? 'Could not initiate prior authorization.'
          : 'Could not send prescription.',
        {
          description:
            sendResult?.kind === 'rejected'
              ? sendResult.message
              : (orchestrator.state.errorMessage ?? undefined),
        }
      );
    }
  };

  const openDecisionDialog = (decision: 'SELF_PAY' | 'EXCEPTIONAL_UNKNOWN_SEND') => {
    setDecisionDialog(decision);
    setDecisionReason('');
    setPatientAgreed(false);
  };

  const confirmDecision = async () => {
    if (!decisionDialog || !decisionReason.trim() || !patientAgreed) {
      return;
    }
    const decision = decisionDialog;
    setDecisionDialog(null);
    await handleSubmit(decision, { reason: decisionReason.trim(), patientAgreed: true });
  };

  return (
    <div className="w-full max-w-3xl">
      {isEditableMode ? (
        <div className="flex flex-col gap-1">
          <h2 className="text-sm font-medium leading-none tracking-tight">
            {stepId === 'confirmation' && wasDiverted
              ? isPriorAuthResolved
                ? PA_CONFIRMATION_TITLE
                : EXCLUDED_CONFIRMATION_TITLE
              : WIZARD_TITLE_BY_STEP[stepId]}
          </h2>
          <div className="pt-3">
            <RxWizardProgress stepId={stepId} />
          </div>
        </div>
      ) : null}

      <div className="mt-6 flex flex-col gap-6">
        {formError ? (
          <Alert variant="destructive">
            <AlertTitle>More information needed</AlertTitle>
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        ) : null}

        {stepId === 'details' ? (
          <DetailsStep
            form={form}
            userId={userId}
            diagnosisRequiredForInsuranceApproval={diagnosisRequiredForInsuranceApproval}
            formularyEligibilityOverride={formularyEligibilityOverride}
            onFormularyEligibilityOverrideChange={setFormularyEligibilityOverride}
          />
        ) : null}

        {stepId === 'review' && blockingPrefillIssue ? (
          <RxPrefillIssueAlert
            issue={blockingPrefillIssue}
            isRetrying={isRetryingPrefill}
            onRetry={retryPrefill}
            onReject={rejectSourceProposedAction}
          />
        ) : null}

        {stepId === 'review' && offerPharmacyPicker && !isDraftLocked ? (
          <PharmacySelector form={form} userId={userId} prepopulateDefault={false} />
        ) : null}

        {stepId === 'review' ? (
          <ReviewStep
            userId={userId}
            form={form}
            errorMessage={submissionState.errorMessage}
            onEditDetails={isEditableMode ? () => setStepId('details') : undefined}
            onEditDiagnosis={isEditableMode ? () => setDiagnosisDialogOpen(true) : undefined}
            readOnly={!isEditableMode}
            paExpedited={paExpedited}
            onPaExpeditedChange={setPaExpedited}
            formularyEligibilityOverride={formularyEligibilityOverride}
            onFormularyEligibilityOverrideChange={setFormularyEligibilityOverride}
          />
        ) : null}

        {stepId === 'confirmation' ? (
          <ConfirmationStep
            submissionState={submissionState}
            onRetryCreate={retryCreate}
            onRetrySend={retrySend}
            isPriorAuth={isPriorAuthResolved}
            isExcluded={isExcludedResolved}
            expedited={paExpedited}
            priorAuthCaseNumber={
              sendDisposition?.kind === 'prior-auth-initiated' ? sendDisposition.caseNumber : null
            }
          />
        ) : null}
      </div>

      <div
        className={
          isEditableMode
            ? 'sticky -bottom-3 z-10 -mx-3 -mb-3 mt-6 flex items-center justify-between border-t border-border/60 bg-card/95 px-3 pb-3 pt-3 backdrop-blur'
            : 'sticky -bottom-3 z-10 -mx-3 -mb-3 mt-7 border-t border-border/50 bg-card/95 px-3 pb-3 pt-3 backdrop-blur'
        }
      >
        {isEditableMode ? (
          <Button
            type="button"
            variant="secondary"
            radius="pill"
            onClick={handleBack}
            disabled={isBackDisabled}
          >
            Back
          </Button>
        ) : (
          <div />
        )}

        <div className={isEditableMode ? 'flex gap-2' : 'flex w-full flex-col gap-1.5'}>
          {sourceProposedActionRef.current ||
          existingRxIdRef.current ||
          submissionState.prescriptionRecordId ? (
            <PrescriptionCancelButton
              patientUserId={userId}
              target={
                submissionState.prescriptionRecordId
                  ? { prescriptionRecordId: submissionState.prescriptionRecordId }
                  : existingRxIdRef.current
                    ? { dosespotPrescriptionId: existingRxIdRef.current }
                    : { proposedActionExternalId: sourceProposedActionRef.current!.externalId }
              }
              disabled={isSubmitting || isHydrating}
              onCancelled={() => overlays.close('rxWizard')}
            />
          ) : null}
          {stepId === 'review' && blockingPrefillIssue ? null : stepId === 'review' ? (
            <>
              {decisionActions
                .filter((decision) => isEditableMode || decision !== 'SELF_PAY')
                .map((decision) => (
                  <Button
                    key={decision}
                    type="button"
                    radius="pill"
                    variant={decision === 'SELF_PAY' ? 'ghost' : 'default'}
                    className={!isEditableMode ? 'w-full' : undefined}
                    onClick={() =>
                      decision === 'START_PA' && isDiagnosisMissingForInsuranceApproval
                        ? setDiagnosisDialogOpen(true)
                        : decision === 'SELF_PAY' || decision === 'EXCEPTIONAL_UNKNOWN_SEND'
                          ? openDecisionDialog(decision)
                          : void handleSubmit(decision)
                    }
                    disabled={
                      isSubmitting || (isDraftLocked && !canChooseUpdatedDecision) || isHydrating
                    }
                    aria-busy={isSubmitting && lastDecision === decision}
                  >
                    {isSubmitting && lastDecision === decision ? (
                      <SubmittingLabel decision={decision} />
                    ) : decision === 'START_PA' ? (
                      'Request insurance approval'
                    ) : decision === 'SELF_PAY' ? (
                      'Use self-pay'
                    ) : decision === 'EXCEPTIONAL_UNKNOWN_SEND' ? (
                      'Send anyway'
                    ) : (
                      'Create & send'
                    )}
                  </Button>
                ))}
              {!isEditableMode && decisionActions.includes('SELF_PAY') ? (
                <Button
                  type="button"
                  radius="pill"
                  variant="outline"
                  className="w-full border-foreground/20 bg-transparent text-foreground shadow-none hover:bg-secondary/40 hover:text-foreground dark:bg-transparent dark:hover:bg-secondary/40"
                  onClick={() => openDecisionDialog('SELF_PAY')}
                  disabled={
                    isSubmitting || (isDraftLocked && !canChooseUpdatedDecision) || isHydrating
                  }
                  aria-busy={isSubmitting && lastDecision === 'SELF_PAY'}
                >
                  {isSubmitting && lastDecision === 'SELF_PAY' ? (
                    <SubmittingLabel decision="SELF_PAY" />
                  ) : (
                    'Use self-pay instead'
                  )}
                </Button>
              ) : null}
              {decisionOutcome === null && priorAuthPreviewQuery.isError ? (
                <Button
                  type="button"
                  radius="pill"
                  variant="secondary"
                  onClick={() => void priorAuthPreviewQuery.refetch()}
                  disabled={priorAuthPreviewQuery.isFetching}
                >
                  {priorAuthPreviewQuery.isFetching ? 'Checking coverage…' : 'Retry coverage check'}
                </Button>
              ) : decisionOutcome === null ? (
                <Button type="button" radius="pill" disabled>
                  Checking coverage…
                </Button>
              ) : null}
            </>
          ) : stepId === 'confirmation' ? (
            <Button
              type="button"
              radius="pill"
              onClick={() => overlays.close()}
              disabled={isSubmitting}
            >
              Finish
            </Button>
          ) : (
            <Button type="button" radius="pill" onClick={() => void handleNext()}>
              Next
            </Button>
          )}
        </div>
      </div>

      {diagnosisDialogOpen ? (
        <DiagnosisSelector
          form={form}
          hideField
          open
          onOpenChange={setDiagnosisDialogOpen}
          dialogTitle="Diagnosis for insurance approval"
          dialogDescription="Insurance uses this diagnosis to decide whether to approve the medication."
        />
      ) : null}

      {decisionDialog ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="rx-decision-title"
        >
          <div className="mx-4 w-full max-w-sm space-y-3 rounded-md border border-border bg-background p-4 shadow-lg">
            <h3 id="rx-decision-title" className="text-sm font-semibold">
              {decisionDialog === 'SELF_PAY'
                ? 'Use self-pay instead'
                : 'Send without confirmed coverage'}
            </h3>
            <p className="text-xs text-muted-foreground">
              Add a short reason and confirm that the patient agreed before sending.
            </p>
            <Textarea
              value={decisionReason}
              onChange={(event) => setDecisionReason(event.target.value)}
              placeholder="Reason"
              rows={3}
            />
            <label className="flex items-center gap-2 text-xs">
              <Checkbox
                checked={patientAgreed}
                onCheckedChange={(checked) => setPatientAgreed(checked === true)}
              />
              The patient agreed.
            </label>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setDecisionDialog(null)}>
                Back
              </Button>
              <Button
                type="button"
                onClick={() => void confirmDecision()}
                disabled={!decisionReason.trim() || !patientAgreed}
              >
                Send prescription as self-pay
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function RxWizard({ userId, prefill }: RxWizardProps) {
  return (
    <AgentTweakableArea prefix="rxWizard">
      <RxWizardInner userId={userId} prefill={prefill} />
    </AgentTweakableArea>
  );
}
